import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export type ProgressEntry = {
  id?: string;
  user_id?: string;
  category: string;
  item_id: string;
  stars: number;
  completed: boolean;
  last_position?: any;
  updated_at?: string;
};

const LS_KEY = "tm_progress_v1";

const readLocal = (): ProgressEntry[] => {
  try {
    return JSON.parse(localStorage.getItem(LS_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeLocal = (rows: ProgressEntry[]) =>
  localStorage.setItem(LS_KEY, JSON.stringify(rows));

export const useProgress = (category?: string) => {
  const { user } = useAuth();
  const [rows, setRows] = useState<ProgressEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    if (user) {
      let q = supabase.from("user_progress").select("*").eq("user_id", user.id);
      if (category) q = q.eq("category", category);
      const { data } = await q.order("updated_at", { ascending: false });
      setRows((data as any) || []);
    } else {
      const local = readLocal();
      setRows(category ? local.filter((r) => r.category === category) : local);
    }
    setLoading(false);
  }, [user, category]);

  useEffect(() => {
    load();
  }, [load]);

  const upsert = useCallback(
    async (entry: Omit<ProgressEntry, "user_id">) => {
      if (user) {
        const { data } = await supabase
          .from("user_progress")
          .upsert(
            { ...entry, user_id: user.id, updated_at: new Date().toISOString() },
            { onConflict: "user_id,category,item_id" }
          )
          .select()
          .maybeSingle();
        if (data) {
          setRows((prev) => {
            const others = prev.filter(
              (r) => !(r.category === entry.category && r.item_id === entry.item_id)
            );
            return [data as any, ...others];
          });
        }
      } else {
        const local = readLocal();
        const others = local.filter(
          (r) => !(r.category === entry.category && r.item_id === entry.item_id)
        );
        const next = [
          { ...entry, updated_at: new Date().toISOString() },
          ...others,
        ];
        writeLocal(next);
        setRows(category ? next.filter((r) => r.category === category) : next);
      }
    },
    [user, category]
  );

  const totalStars = rows.reduce((s, r) => s + (r.stars || 0), 0);
  const completedCount = rows.filter((r) => r.completed).length;
  const last = rows[0];

  return { rows, loading, upsert, totalStars, completedCount, last, reload: load };
};
